/**
 * Unified Web Design System for Survey Connect
 * 
 * This file provides consistent CSS-in-JS patterns for web components.
 * Import and use these to ensure visual uniformity across all portals.
 */
import React from 'react';

/* ─── Design Tokens (mapped to ThemeProvider CSS variables) ─────── */
const colors = {
  primary: 'var(--sc-brand-primary)',
  primaryLight: 'var(--sc-brand-primary-light)',
  primaryDark: 'var(--sc-brand-primary-dark)',
  secondary: 'var(--sc-success)',
  success: 'var(--sc-success)',
  warning: 'var(--sc-warning)',
  error: 'var(--sc-error)',
  info: 'var(--sc-info)',
  background: 'var(--sc-bg-primary)',  // Dynamic backdrop
  surface: 'var(--sc-bg-secondary)',   // Dynamic surface
  text: 'var(--sc-text-primary)',      // Dynamic text
  textSecondary: 'var(--sc-text-secondary)',
  textLight: 'var(--sc-text-tertiary)',
  textOnPrimary: 'var(--sc-text-on-brand)',
  border: 'var(--sc-border)',
  divider: 'var(--sc-divider)',
};

const spacing = { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 };
const borderRadius = { sm: 8, md: 12, lg: 16, xl: 20 };

/* ─── Typography ────────────────────────────────────────────────── */
export const typography = {
  pageTitle: {
    fontSize: 28,
    fontWeight: 800,
    color: colors.text,
    margin: '0 0 6px',
    letterSpacing: '-0.5px',
    fontFamily: "'Outfit', 'Inter', -apple-system, sans-serif",
  } as React.CSSProperties,

  pageSubtitle: {
    fontSize: 14,
    color: colors.textSecondary,
    margin: '0 0 28px',
    fontWeight: 400,
    lineHeight: 1.5,
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: colors.text,
    margin: '0 0 18px',
    letterSpacing: '-0.2px',
    fontFamily: "'Outfit', 'Inter', -apple-system, sans-serif",
  } as React.CSSProperties,

  cardTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: colors.text,
    margin: '0 0 14px',
    letterSpacing: '-0.1px',
  } as React.CSSProperties,

  label: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textSecondary,
    marginBottom: 8,
    display: 'block',
    letterSpacing: '0.2px',
  } as React.CSSProperties,

  smallLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: colors.textSecondary,
    letterSpacing: '0.1px',
  } as React.CSSProperties,

  body: {
    fontSize: 14,
    color: colors.text,
    lineHeight: 1.6,
  } as React.CSSProperties,

  bodySmall: {
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 1.5,
  } as React.CSSProperties,

  link: {
    color: colors.primary,
    fontWeight: 600,
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
  } as React.CSSProperties,
};

/* ─── Card Patterns ─────────────────────────────────────────────── */
export const cards = {
  base: {
    background: colors.surface,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    border: `1px solid ${colors.border}`,
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03), 0 1px 2px rgba(15, 23, 42, 0.02)',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  } as React.CSSProperties,

  flat: {
    background: colors.surface,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    border: `1px solid ${colors.border}`,
    transition: 'all 0.2s ease',
  } as React.CSSProperties,

  elevated: {
    background: colors.surface,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    boxShadow: '0 10px 25px rgba(15, 23, 42, 0.05), 0 2px 8px rgba(15, 23, 42, 0.03)',
    border: `1px solid ${colors.border}`,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  } as React.CSSProperties,

  kpi: {
    display: 'flex',
    flexDirection: 'column' as const,
    background: colors.surface,
    borderRadius: borderRadius.lg,
    padding: '20px 22px',
    border: `1px solid ${colors.border}`,
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03), 0 1px 2px rgba(15, 23, 42, 0.02)',
    position: 'relative' as const,
    overflow: 'hidden' as const,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  } as React.CSSProperties,

  analytic: {
    background: colors.surface,
    borderRadius: borderRadius.lg,
    padding: 24,
    border: `1px solid ${colors.border}`,
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03), 0 1px 2px rgba(15, 23, 42, 0.02)',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  } as React.CSSProperties,

  subtle: {
    background: colors.background,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    border: '1px solid rgba(226, 232, 240, 0.5)',
  } as React.CSSProperties,
};

/* ─── Buttons ───────────────────────────────────────────────────── */
export const buttons = {
  primary: {
    padding: '10px 20px',
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
    color: colors.textOnPrimary,
    border: 'none',
    borderRadius: borderRadius.md,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: `0 4px 12px rgba(12, 90, 219, 0.25), 0 1px 2px rgba(0,0,0,0.05)`,
  } as React.CSSProperties,

  secondary: {
    padding: '10px 20px',
    background: colors.surface,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
  } as React.CSSProperties,

  ghost: {
    padding: '8px 14px',
    background: 'transparent',
    color: colors.textSecondary,
    border: 'none',
    borderRadius: borderRadius.md,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    transition: 'all 0.2s ease',
  } as React.CSSProperties,

  danger: {
    padding: '10px 20px',
    background: `linear-gradient(135deg, ${colors.error} 0%, #DC2626 100%)`,
    color: colors.textOnPrimary,
    border: 'none',
    borderRadius: borderRadius.md,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
  } as React.CSSProperties,

  link: {
    padding: 0,
    background: 'none',
    color: colors.primary,
    border: 'none',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  } as React.CSSProperties,

  icon: {
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    fontSize: 16,
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
  } as React.CSSProperties,

  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 8,
    border: '1px solid transparent',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    color: colors.textSecondary,
    fontFamily: "'Inter', sans-serif",
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  } as React.CSSProperties,

  tabActive: {
    background: colors.surface,
    color: colors.primary,
    border: `1px solid ${colors.border}`,
    fontWeight: 600,
    boxShadow: '0 2px 4px 0 rgba(0,0,0,0.03)',
  } as React.CSSProperties,
};

/* ─── Form Inputs ───────────────────────────────────────────────── */
export const inputs = {
  text: {
    width: '100%',
    padding: '10px 14px',
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    outline: 'none',
    transition: 'all 0.2s ease',
    background: colors.surface,
    color: colors.text,
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
  } as React.CSSProperties,

  search: {
    width: '100%',
    padding: '8px 14px 8px 38px',
    fontSize: 13,
    fontFamily: "'Inter', sans-serif",
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    outline: 'none',
    transition: 'all 0.2s ease',
    background: colors.surface,
    color: colors.text,
  } as React.CSSProperties,

  select: {
    width: '100%',
    padding: '10px 14px',
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    outline: 'none',
    transition: 'all 0.2s ease',
    background: colors.surface,
    color: colors.text,
    cursor: 'pointer',
    appearance: 'none' as const,
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
  } as React.CSSProperties,

  textarea: {
    width: '100%',
    padding: '10px 14px',
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    outline: 'none',
    transition: 'all 0.2s ease',
    background: colors.surface,
    color: colors.text,
    resize: 'vertical' as const,
    minHeight: 100,
    lineHeight: 1.5,
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
  } as React.CSSProperties,

  focusStyles: {
    borderColor: colors.primary,
    boxShadow: `0 0 0 3px ${colors.primary}20`,
  } as React.CSSProperties,
};

/* ─── Layout Patterns ───────────────────────────────────────────── */
export const layout = {
  page: {
    padding: spacing.lg,
    maxWidth: 1400,
    margin: '0 auto',
  } as React.CSSProperties,

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: spacing.lg,
    flexWrap: 'wrap' as const,
    gap: spacing.md,
  } as React.CSSProperties,

  row: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
  } as React.CSSProperties,

  column: {
    display: 'flex',
    flexDirection: 'column' as const,
  } as React.CSSProperties,

  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: spacing.md,
  } as React.CSSProperties,

  grid3: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: spacing.md,
  } as React.CSSProperties,

  grid4: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: spacing.md,
  } as React.CSSProperties,

  grid6: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: 14,
  } as React.CSSProperties,

  center: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,

  spaceBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as React.CSSProperties,
};

/* ─── Table Patterns ────────────────────────────────────────────── */
export const table = {
  container: {
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    overflow: 'hidden',
  } as React.CSSProperties,

  header: {
    display: 'flex',
    padding: '12px 16px',
    background: colors.background,
    borderBottom: `1px solid ${colors.border}`,
  } as React.CSSProperties,

  th: {
    flex: 1,
    fontSize: 11,
    fontWeight: 700,
    color: colors.textSecondary,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  } as React.CSSProperties,

  row: {
    display: 'flex',
    padding: '14px 16px',
    borderBottom: `1px solid ${colors.divider}`,
    alignItems: 'center',
    transition: 'background 0.15s',
    cursor: 'pointer',
  } as React.CSSProperties,

  rowHover: {
    background: colors.background,
  } as React.CSSProperties,

  td: {
    flex: 1,
    fontSize: 13,
    color: colors.text,
  } as React.CSSProperties,

  tdSecondary: {
    flex: 1,
    fontSize: 13,
    color: colors.textSecondary,
  } as React.CSSProperties,
};

/* ─── Badge / Status Patterns ───────────────────────────────────── */
export const badges = {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.3,
  } as React.CSSProperties,

  success: {
    background: '#D1FAE5',
    color: '#065F46',
  } as React.CSSProperties,

  warning: {
    background: '#FEF3C7',
    color: '#92400E',
  } as React.CSSProperties,

  error: {
    background: '#FEE2E2',
    color: '#B91C1C',
  } as React.CSSProperties,

  info: {
    background: '#DBEAFE',
    color: '#1E40AF',
  } as React.CSSProperties,

  neutral: {
    background: '#F1F5F9',
    color: '#475569',
  } as React.CSSProperties,

  purple: {
    background: '#EDE9FE',
    color: '#7C3AED',
  } as React.CSSProperties,

  orange: {
    background: '#FFEDD5',
    color: '#C2410C',
  } as React.CSSProperties,

  teal: {
    background: '#E8F1FD',
    color: '#0D9488',
  } as React.CSSProperties,
};

/* ─── Icons ─────────────────────────────────────────────────────── */
const ICON_CIRCLE_BASE: React.CSSProperties = {
  borderRadius: '50%',
  background: '#F0F2F5',
  boxShadow: '6px 6px 14px rgba(163,177,198,0.35), -4px -4px 10px rgba(255,255,255,0.9)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export const iconBox = {
  sm: {
    ...ICON_CIRCLE_BASE,
    width: 36,
    height: 36,
    fontSize: 16,
  } as React.CSSProperties,

  md: {
    ...ICON_CIRCLE_BASE,
    width: 44,
    height: 44,
    fontSize: 20,
  } as React.CSSProperties,

  lg: {
    ...ICON_CIRCLE_BASE,
    width: 56,
    height: 56,
    fontSize: 26,
  } as React.CSSProperties,
};

/* ─── Loading / Spinner ─────────────────────────────────────────── */
export const loading = {
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #E0E0E0',
    borderTopColor: colors.primary,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  } as React.CSSProperties,

  spinnerLg: {
    width: 40,
    height: 40,
    border: '4px solid #E0E0E0',
    borderTopColor: colors.primary,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  } as React.CSSProperties,

  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
  } as React.CSSProperties,

  fullPage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
  } as React.CSSProperties,
};

/* ─── Empty State ───────────────────────────────────────────────── */
export const emptyState = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
    textAlign: 'center' as const,
  } as React.CSSProperties,

  icon: {
    fontSize: 56,
    marginBottom: 16,
    opacity: 0.8,
  } as React.CSSProperties,

  title: {
    fontSize: 18,
    fontWeight: 700,
    color: colors.text,
    margin: '0 0 8px',
  } as React.CSSProperties,

  text: {
    fontSize: 14,
    color: colors.textSecondary,
    maxWidth: 400,
    lineHeight: 1.6,
    margin: '0 0 20px',
  } as React.CSSProperties,
};

/* ─── Progress / Charts ─────────────────────────────────────────── */
export const charts = {
  barTrack: {
    flex: 1,
    height: 26,
    background: 'linear-gradient(90deg, #F0F4FF 0%, #EEF2FF 100%)',
    borderRadius: 13,
    overflow: 'hidden',
    boxShadow: 'inset 0 2px 5px rgba(99,102,241,0.07)',
  } as React.CSSProperties,

  barFill: {
    height: '100%',
    borderRadius: 13,
    minWidth: 6,
    transition: 'width 0.4s ease',
    transformOrigin: 'left center',
  } as React.CSSProperties,

  progressTrack: {
    height: 8,
    background: colors.background,
    borderRadius: 4,
    overflow: 'hidden',
  } as React.CSSProperties,

  progressFill: {
    height: '100%',
    borderRadius: 4,
    transition: 'width 0.4s ease',
  } as React.CSSProperties,
};

/* ─── Modal / Dialog Patterns ───────────────────────────────────── */
export const modal = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: spacing.lg,
  } as React.CSSProperties,

  container: {
    background: colors.surface,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    maxWidth: 480,
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  } as React.CSSProperties,

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
  } as React.CSSProperties,

  title: {
    fontSize: 18,
    fontWeight: 700,
    color: colors.text,
    margin: 0,
  } as React.CSSProperties,

  body: {
    marginBottom: spacing.lg,
  } as React.CSSProperties,

  footer: {
    display: 'flex',
    gap: spacing.sm,
    justifyContent: 'flex-end',
  } as React.CSSProperties,
};

/* ─── KPI / Metrics ─────────────────────────────────────────────── */
export const kpi = {
  value: {
    fontSize: 22,
    fontWeight: 800,
    color: colors.text,
    marginBottom: 4,
  } as React.CSSProperties,

  label: {
    fontSize: 11,
    fontWeight: 500,
    color: colors.textSecondary,
  } as React.CSSProperties,

  bigValue: {
    fontSize: 28,
    fontWeight: 800,
    color: colors.text,
  } as React.CSSProperties,

  trend: {
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  } as React.CSSProperties,

  trendUp: {
    color: colors.success,
  } as React.CSSProperties,

  trendDown: {
    color: colors.error,
  } as React.CSSProperties,
};

/* ─── Avatar ────────────────────────────────────────────────────── */
export const avatar = {
  sm: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700,
    color: colors.textOnPrimary,
    background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`,
  } as React.CSSProperties,

  md: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    color: colors.textOnPrimary,
    background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`,
  } as React.CSSProperties,

  lg: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 700,
    color: colors.textOnPrimary,
    background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`,
  } as React.CSSProperties,
};

/* ─── Status Colors Mapping ─────────────────────────────────────── */
export const statusColors: Record<string, { bg: string; color: string }> = {
  REQUEST_SUBMITTED: { bg: '#F1F5F9', color: '#64748B' },
  UNDER_REVIEW: { bg: '#DBEAFE', color: '#1E40AF' },
  APPROVED: { bg: '#D1FAE5', color: '#065F46' },
  ASSIGNED: { bg: '#EDE9FE', color: '#7C3AED' },
  NEGOTIATING: { bg: '#FFF7ED', color: '#EA580C' },
  CONFIRMED: { bg: '#E8F1FD', color: '#0D9488' },
  CLOSED: { bg: '#F1F5F9', color: '#64748B' },
  DISPUTED: { bg: '#FEE2E2', color: '#B91C1C' },
  PENDING: { bg: '#FEF3C7', color: '#92400E' },
  ACTIVE: { bg: '#D1FAE5', color: '#065F46' },
  INACTIVE: { bg: '#F1F5F9', color: '#64748B' },
  SUSPENDED: { bg: '#FEE2E2', color: '#B91C1C' },
  GOOD: { bg: '#D1FAE5', color: '#065F46' },
  OVERDUE: { bg: '#FFEDD5', color: '#C2410C' },
  DEFAULTED: { bg: '#FEE2E2', color: '#B91C1C' },
};

/* ─── Utility Helpers ───────────────────────────────────────────── */
export const utils = {
  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  noSelect: {
    userSelect: 'none' as const,
  } as React.CSSProperties,

  clickable: {
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  } as React.CSSProperties,

  disabled: {
    opacity: 0.5,
    pointerEvents: 'none' as const,
    cursor: 'not-allowed',
  } as React.CSSProperties,
};

/* ─── Premium Mobile Layout Patterns ────────────────────────────── */
export const mobile = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: '100vh',
    width: '100%',
    maxWidth: 480, // Lock mobile view on desktop
    margin: '0 auto',
    background: 'var(--sc-bg-primary)',
    position: 'relative' as const,
    boxShadow: 'var(--sc-shadow-xl)',
    overflowX: 'hidden' as const,
  } as React.CSSProperties,

  header: {
    height: 'var(--sc-header-height)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    background: 'var(--sc-bg-secondary)',
    borderBottom: '1px solid var(--sc-border-light)',
    position: 'sticky' as const,
    top: 0,
    zIndex: 100,
    paddingTop: 'env(safe-area-inset-top, 0px)',
  } as React.CSSProperties,

  content: {
    flex: 1,
    padding: 16,
    overflowY: 'auto' as const,
    paddingBottom: 'calc(var(--sc-tab-bar-height) + env(safe-area-inset-bottom, 16px))',
  } as React.CSSProperties,

  bottomNav: {
    position: 'fixed' as const,
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    maxWidth: 480,
    height: 'var(--sc-tab-bar-height)',
    background: 'var(--sc-bg-tab-bar)',
    backdropFilter: 'blur(10px)',
    borderTop: '1px solid var(--sc-border-light)',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    boxShadow: 'var(--sc-shadow-tab-bar)',
    zIndex: 200,
  } as React.CSSProperties,

  bottomNavItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    color: 'var(--sc-text-secondary)',
    fontSize: 11,
    fontWeight: 500,
    cursor: 'pointer',
    flex: 1,
    height: '100%',
    border: 'none',
    background: 'transparent',
    transition: 'all 0.2s ease',
  } as React.CSSProperties,

  bottomNavItemActive: {
    color: 'var(--sc-brand-primary)',
    fontWeight: 600,
  } as React.CSSProperties,
};

/* ─── Bottom Sheets / Action Sheets ──────────────────────────────── */
export const sheets = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'var(--sc-bg-overlay)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    animation: 'fadeIn 0.25s ease-out',
  } as React.CSSProperties,

  container: {
    width: '100%',
    maxWidth: 480,
    background: 'var(--sc-bg-sheet)',
    borderTopLeftRadius: 'var(--sc-radius-2xl)',
    borderTopRightRadius: 'var(--sc-radius-2xl)',
    padding: '16px 20px 24px',
    boxShadow: 'var(--sc-shadow-xl)',
    animation: 'slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
    paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
  } as React.CSSProperties,

  handle: {
    width: 36,
    height: 5,
    borderRadius: 2.5,
    background: 'var(--sc-border)',
    margin: '0 auto 16px',
  } as React.CSSProperties,

  title: {
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--sc-text-primary)',
    marginBottom: 8,
    textAlign: 'center' as const,
  } as React.CSSProperties,
};

/* ─── Export all for convenient importing ───────────────────────── */
export const webStyles = {
  typography,
  cards,
  buttons,
  inputs,
  layout,
  table,
  badges,
  iconBox,
  loading,
  emptyState,
  charts,
  modal,
  kpi,
  avatar,
  statusColors,
  utils,
  mobile,
  sheets,
};

export default webStyles;

